import { Resend } from 'resend';
import { validateEmail, validatePhone } from './validation';

const resend = new Resend(process.env.REACT_APP_RESEND_API_KEY);

// Enhanced context gathering
const getBaseErrorContext = (error, additionalContext = {}) => ({
  timestamp: new Date().toISOString(),
  error: {
    message: error.message,
    stack: error.stack,
    name: error.name,
    componentStack: error.componentStack
  },
  browser: {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    vendor: navigator.vendor,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    onLine: navigator.onLine
  },
  window: {
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
    location: window.location.href,
    origin: window.location.origin,
    pathname: window.location.pathname,
    search: window.location.search
  },
  performance: {
    timeOrigin: performance?.timeOrigin,
    memory: performance?.memory ? {
      jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
      totalJSHeapSize: performance.memory.totalJSHeapSize,
      usedJSHeapSize: performance.memory.usedJSHeapSize
    } : null,
    navigation: performance?.getEntriesByType('navigation')?.[0],
    timing: performance?.timing
  },
  screen: {
    width: window.screen.width,
    height: window.screen.height,
    colorDepth: window.screen.colorDepth,
    orientation: window.screen.orientation?.type
  },
  ...additionalContext
});

// Unified error logging function
export async function logError(error, context = {}, type = 'client') {
  try {
    const errorDetails = getBaseErrorContext(error, context);
    
    const response = await fetch(`https://prepory-form-backend-ea8tc.kinsta.app/log-error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...errorDetails,
        errorType: type
      })
    });

    if (!response.ok) {
      console.error('Failed to log error:', await response.text());
    }
  } catch (emailError) {
    console.error('Failed to send error email:', emailError);
  }
}

// Enhanced validation error context
const getValidationErrorContext = (error, formData, validationType, formState) => ({
  formData: {
    ...formData,
    fieldLengths: Object.entries(formData).reduce((acc, [key, value]) => ({
      ...acc,
      [key]: value?.length || 0
    }), {})
  },
  validationType,
  formState,
  validationState: {
    emailValid: formData.email ? validateEmail(formData.email) : null,
    phoneValid: formData.phone ? validatePhone(formData.phone) : null,
    emailSubValid: formData.emailSub ? validateEmail(formData.emailSub) : null,
    phoneSubValid: formData.phoneSub ? validatePhone(formData.phoneSub) : null,
    recaptchaComplete: formData.recaptchaComplete
  },
  formMetrics: {
    totalFields: Object.keys(formData).length,
    filledFields: Object.values(formData).filter(v => v && v.length > 0).length,
    emptyRequiredFields: Object.entries(formData)
      .filter(([key]) => ['firstName', 'lastName', 'email', 'phone'].includes(key))
      .filter(([_, value]) => !value || value.length === 0)
      .map(([key]) => key)
  }
});

// Enhanced form error logger
export async function logFormError(error, formData, validationType, formState) {
  return logError(
    error,
    getValidationErrorContext(error, formData, validationType, formState),
    'form_validation'
  );
}

// Global error handlers
window.onerror = (msg, url, line, col, error) => {
  logError(error || new Error(msg), {
    type: 'uncaught_error',
    source: url,
    line,
    column: col
  });
  return true;
};

window.onunhandledrejection = (event) => {
  logError(event.reason || new Error('Unhandled Promise Rejection'), {
    type: 'promise_rejection'
  });
  event.preventDefault();
};
